<template>
  <RevBreadcrumbs
    :ariaLabel="i18n(translations.breadcrumbs)"
    :breads
    class="max-w-[1280px] w-full m-auto px-16 md:px-24 lg:px-72"
  />
  <div class="py-16 md:py-32 px-24 max-w-[928px] m-auto">
    <ErrorContainer v-if="journeysError" @retry="refreshJourneys" />
    <div
      v-else-if="journey"
      class="flex flex-col md:flex-row gap-16 md:gap-32 justify-center"
    >
      <div class="basis-1/2">
        <RevTag
          v-if="journey.deliveryTag !== 'UNTRACKED'"
          class="mb-12"
          data-test="delivery-tag"
          :label="deliveryTagLabel"
          :variant="deliveryTagVariant"
        />
        <dl>
          <div v-if="shouldDisplayDeliveryDate" class="mb-12">
            <dt class="caption block">
              {{ i18n(translations.estimatedDelivery) }}
            </dt>
            <dd class="punchline-sans block first-letter:capitalize">
              <DeliveryDate
                :estimated-delivery-date="journey.estimatedDeliveryDate"
                :promised-max-delivery-date="journey.promisedMaxDeliveryDate"
                :promised-min-delivery-date="journey.promisedMinDeliveryDate"
              />
            </dd>
            <dd
              v-if="journey.estimatedDeliveryDate"
              class="line-through body-1 first-letter:capitalize"
            >
              <DeliveryDate
                :estimated-delivery-date="journey.previousEstimatedDeliveryDate"
                :promised-max-delivery-date="journey.promisedMaxDeliveryDate"
                :promised-min-delivery-date="journey.promisedMinDeliveryDate"
              />
            </dd>
          </div>

          <div class="mb-12">
            <dt class="caption block">
              {{ i18n(translations.deliveryBy) }}
            </dt>
            <dd class="body-1-bold block">{{ journey.carrierName }}</dd>
          </div>

          <div class="mb-12">
            <dt class="caption block">{{ i18n(translations.trackingId) }}</dt>
            <dd class="body-1-bold block">
              <RevLink
                v-if="journey.trackingUrl"
                class="inline-flex items-center gap-2 mb-4"
                rel="noopener noreferrer"
                target="_blank"
                :to="journey.trackingUrl"
                @click="trackOpenCarrierTracking"
              >
                {{ journey.trackingNumber }}
                <IconExternalLink size="24" />
              </RevLink>
              <template v-else>
                {{ journey.trackingNumber }}
              </template>
            </dd>
          </div>

          <div>
            <dt class="caption block">
              {{ i18n(translations.needHelpLabel) }}
            </dt>
            <dd class="body-1 text-static-default-hi">
              <FormattedMessage :definition="translations.needHelpDescription">
                <template #contactCarrierLink>
                  <RevLink
                    v-if="journey.trackingUrl"
                    class="inline-flex items-center gap-2"
                    rel="noopener noreferrer"
                    target="_blank"
                    :to="journey.trackingUrl"
                  >
                    {{
                      i18n(translations.contactCarrier, {
                        carrierName: journey.carrierName,
                      })
                    }}
                    <IconExternalLink size="16" />
                  </RevLink>
                  <template v-else>
                    {{
                      i18n(translations.contactCarrier, {
                        carrierName: journey.carrierName,
                      })
                    }}
                  </template>
                </template>
                <template #contactBackMarketLink>
                  <RevLink :to="backMarketContactUrl">
                    {{ i18n(translations.contactBackMarket) }}
                  </RevLink>
                </template>
              </FormattedMessage>
            </dd>
          </div>
        </dl>
      </div>
      <div class="basis-1/2">
        <RevInfoBlock
          v-if="isDelayedOrUntracked"
          class="mb-12"
          :content="i18n(translations.delayedAlertDescription)"
          :icon="IconWarning"
          :title="
            i18n(translations.delayedAlertTitle, {
              carrierName: journey.carrierName,
            })
          "
          variant="warning"
        >
          <RevButton
            v-if="journey.trackingUrl"
            rel="noopener noreferrer"
            size="small"
            target="_blank"
            :to="journey.trackingUrl"
            variant="primary"
            @click="trackOpenCarrierTracking"
          >
            {{
              i18n(translations.delayedAlertButton, {
                carrierName: journey.carrierName,
              })
            }}
            <IconExternalLink class="inline" size="20" />
          </RevButton>
        </RevInfoBlock>

        <RevCard v-if="journey.steps?.length" class="p-16">
          <DeliveryTimeline
            :current-step="currentStepKey"
            :steps="statusTrackerSteps"
          />

          <RevDialog
            name="all-delivery-updates-modal"
            :title="i18n(translations.allUpdates)"
          >
            <template #trigger="{ open }">
              <RevButton
                class="w-full"
                variant="secondary"
                @click="
                  () => {
                    trackSeeAllUpdates()
                    open()
                  }
                "
              >
                {{ i18n(translations.seeAllUpdates) }}
              </RevButton>
            </template>
            <template #body>
              <RevCard class="p-16 md:max-w-400">
                <DeliveryTimeline
                  :current-step="currentCheckpointKey"
                  :steps="detailedStatusTrackerSteps"
                />
              </RevCard>
            </template>
          </RevDialog>
        </RevCard>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute, useRouteLocationWithLocale } from '#imports'
import { computed } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevBreadcrumbs } from '@ds/components/Breadcrumbs'
import type { Bread } from '@ds/components/Breadcrumbs/Breadcrumbs.vue'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDialog } from '@ds/components/Dialog'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'
import { IconExternalLink } from '@ds/icons/IconExternalLink'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Checkpoint, Journey, Step } from '~/api/tracking/types'
import ErrorContainer from '~/scopes/care-commons/components/ErrorContainer/ErrorContainer.vue'
import { useOrderlineDetails } from '~/scopes/care-commons/components/order/OrderlineCard/composables/useOrderlineDetails'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'

import {
  CARRIER_TRACKING_URL_TRACK_CLICK_NAME,
  PARCEL_JOURNEY_TYPE_FORWARD,
  SEE_ALL_UPDATES_CTA_TRACK_CLICK_NAME,
  TRACK_PARCEL_TRACK_CLICK_TARGET_INTERNAL,
  TRACK_PARCEL_ZONE,
} from './TrackParcel.constants'
import translations from './TrackParcel.translations'
import DeliveryDate from './components/DeliveryDate/DeliveryDate.vue'
import type { StatusTrackerStep } from './components/DeliveryTimeline/DeliveryTimeline.types'
import DeliveryTimeline from './components/DeliveryTimeline/DeliveryTimeline.vue'

const route = useRoute()
const $httpFetch = useHttpFetch()
const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const { trackClick } = useTracking()

const orderlinePublicId = computed(
  () => route.params.orderlinePublicId as string,
)
const { fetchOrderlineDetails } = useOrderlineDetails()
const orderlineDetailsKey = getAsyncDataKey('orderlineDetails', {
  orderlineId: orderlinePublicId.value,
})

const { data: orderlineDetails } = await useAsyncData(orderlineDetailsKey, () =>
  fetchOrderlineDetails(orderlinePublicId.value),
)

const orderlineId = computed(() => orderlineDetails.value?.orderlineId)

const breads: Array<Bread> = [
  {
    title: i18n(translations.breadcrumbOrders),
    link: resolveLocalizedRoute({ name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.SELF }),
  },
  {
    title: i18n(translations.breadcrumbTrackShipment),
    link: resolveLocalizedRoute({
      name: ROUTE_NAMES.DASHBOARD.MY_ORDERS.TRACK_PARCEL,
    }),
  },
]

const {
  data: journeys,
  error: journeysError,
  refresh: refreshJourneys,
} = await useAsyncData<Journey[]>(
  `tracking-journey-${orderlinePublicId.value}`,
  () =>
    $httpFetch('/tracking/api/parcels/journeys', {
      query: {
        entityId: orderlinePublicId.value,
        entityType: 'SALES_ORDERLINE',
      },
    }),
)

// Get the first journey (For now the endpoint returns only 1 element)
const journey = computed(() => journeys.value?.[0])

const deliveryTagVariant = computed(() => {
  if (!journey.value) return 'success'

  switch (journey.value.deliveryTag) {
    case 'ON_TRACK':
    case 'DELIVERED':
      return 'success'
    case 'UPDATED':
    case 'DELAYED':
      return 'warning'
    case 'FAILED':
      return 'danger'
    default:
      return 'success'
  }
})

const deliveryTagLabel = computed(() => {
  if (!journey.value) return ''

  switch (journey.value.deliveryTag) {
    case 'ON_TRACK':
      return i18n(translations.deliveryOnTrack)
    case 'UPDATED':
      return i18n(translations.updatedDeliveryDate)
    case 'DELAYED':
      return i18n(translations.deliveryDelayed)
    case 'FAILED':
      return i18n(translations.deliveryFailed)
    case 'DELIVERED':
      return i18n(translations.delivered)
    default:
      return ''
  }
})

const shouldDisplayDeliveryDate = computed(() => {
  if (!journey.value) return false

  return journey.value.deliveryTag !== 'DELAYED'
})

const backMarketContactUrl = computed(() => {
  if (!orderlineId.value) return ''

  return resolveLocalizedRoute({
    name: CUSTOMER_CARE.DIAGNOSIS.SELF,
    params: {
      orderlineId: orderlineId.value,
    },
  })
})

const isDelayedOrUntracked = computed(
  () =>
    journey.value?.deliveryTag === 'DELAYED' ||
    journey.value?.deliveryTag === 'UNTRACKED',
)

function trackOpenCarrierTracking() {
  trackClick({
    name: CARRIER_TRACKING_URL_TRACK_CLICK_NAME,
    zone: TRACK_PARCEL_ZONE,
    values: {
      journey: PARCEL_JOURNEY_TYPE_FORWARD,
      orderlinePublicId: orderlinePublicId.value,
      target: TRACK_PARCEL_TRACK_CLICK_TARGET_INTERNAL,
    },
  })
}

function mapStatusTrackerStep(
  item: Step | Checkpoint,
  key: string,
): StatusTrackerStep {
  return {
    key,
    name: item.name,
    date: item.date,
    address: item.address,
  }
}

const statusTrackerSteps = computed(() => {
  if (!journey.value?.steps) return []

  return journey.value.steps.map((step, index) =>
    mapStatusTrackerStep(step, `step-${index}`),
  )
})

const currentStepIndex = computed(() => {
  if (!journey.value?.steps) return 0

  // IRL, API would always return "order placed" step with completed = true at least
  // So lastCompletedIndex is >= 0
  return journey.value.steps.findLastIndex((step) => step.completed)
})

const currentStepKey = computed(() => {
  return `step-${currentStepIndex.value}`
})

const currentCheckpointKey = computed(() => {
  if (!journey.value?.steps) return currentStepKey.value

  const lastCompletedStepCheckpoints =
    journey.value.steps[currentStepIndex.value].checkpoints
  if (!lastCompletedStepCheckpoints.length) return currentStepKey.value

  return `checkpoint-${currentStepIndex.value}-${lastCompletedStepCheckpoints.length - 1}`
})

const currentDeliveryStepName = computed(() => {
  if (!journey.value?.steps) return ''

  return journey.value.steps[currentStepIndex.value].name
})

const detailedStatusTrackerSteps = computed(() => {
  if (!journey.value?.steps) return []

  return journey.value.steps.flatMap((step, stepIndex) =>
    [
      !step.completed && mapStatusTrackerStep(step, `step-${stepIndex}`),
      ...step.checkpoints.map((checkpoint, checkpointIndex) =>
        mapStatusTrackerStep(
          checkpoint,
          `checkpoint-${stepIndex}-${checkpointIndex}`,
        ),
      ),
      // Filtering the completed steps to avoid duplicates between steps and checkpoints
    ].filter(Boolean),
  ) as Array<StatusTrackerStep>
})

function trackSeeAllUpdates() {
  trackClick({
    name: SEE_ALL_UPDATES_CTA_TRACK_CLICK_NAME,
    zone: TRACK_PARCEL_ZONE,
    values: {
      journey: PARCEL_JOURNEY_TYPE_FORWARD,
      orderlinePublicId: orderlinePublicId.value,
      currentDeliveryStep: currentDeliveryStepName.value,
    },
  })
}
</script>
